import { useCallback, useMemo } from 'react';

import { type ApolloQueryResult, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { EnterpriseBadge_UserState as EnterpriseBadgeUserState } from '__generated__/graphql-types';

import VerifiedSkillsEnterpriseBadgeCollectionsQuery from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/VerifiedSkillsEnterpriseBadgeCollectionsQuery.graphql';
import type {
  VerifiedSkillsEnterpriseBadgeCollectionsQuery as QueryResult,
  VerifiedSkillsEnterpriseBadgeCollectionsQueryVariables as QueryVariables,
} from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsEnterpriseBadgeCollectionsQuery';

export type Props = {
  userId: number;
  courseId: string;
  programId: string;
};

const useGetVerifiedSkillsEnterpriseBadgeCollection = ({ userId, courseId, programId }: Props) => {
  const { data, loading, error, refetch } = useQuery<QueryResult, QueryVariables>(
    VerifiedSkillsEnterpriseBadgeCollectionsQuery,
    {
      variables: {
        userId: userId.toString(),
        programId: String(programId),
      },
      context: {
        clientName: 'gatewayGql',
      },
      skip: !programId,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    }
  );

  const badgeCollectionsWithUserState = data?.EnterpriseBadge?.findByUserAndProgram?.badgeCollectionsWithUserState;

  const enrolledBadgeCollection = useMemo(() => {
    if (error) {
      return null;
    }

    if (!badgeCollectionsWithUserState?.length || !courseId) {
      return undefined;
    }

    return badgeCollectionsWithUserState.find((collection) => {
      if (
        (collection?.userState !== EnterpriseBadgeUserState.Enrolled &&
          collection?.userState !== EnterpriseBadgeUserState.Issued) ||
        !collection?.productIds?.length
      ) {
        return false;
      }

      const productIdSet = new Set(collection.productIds.map((product) => product?.id).filter(Boolean));

      return productIdSet.has(courseId);
    });
  }, [badgeCollectionsWithUserState, courseId, error]);

  const is404Error = Boolean(error?.graphQLErrors?.some((err) => err.extensions?.code === 'NOT_FOUND'));

  const refetchEnrolledBadgeCollection = useCallback<
    (variables?: QueryVariables) => Promise<ApolloQueryResult<QueryResult> | undefined>
  >(
    (variables) => {
      // query fails when programId is not set e.g when superuser is accessing the assignment outside a program, so we need to skip the refetch
      if (!programId) {
        return Promise.resolve(undefined);
      }
      return refetch(variables);
    },
    [programId, refetch]
  );

  if (error) {
    if (!is404Error) {
      Sentry.captureException(error);
    }

    return {
      loading: false,
      enrolledBadgeCollection: null,
    };
  }

  return {
    loading,
    enrolledBadgeCollection,
    refetchEnrolledBadgeCollection,
  };
};

export default useGetVerifiedSkillsEnterpriseBadgeCollection;
